import { HSLA, HSVA } from '@ctrl/tinycolor'
import React, { useCallback, useEffect, useMemo } from 'react'
import { ReactColorChangeResult } from 'src/shared/types/color-typings'

export interface ColorPickerSaturationProps {
  hsl: HSLA
  hsv: HSVA
  onChange: (colorChangeResult: ReactColorChangeResult) => void
}

export const ColorPickerSaturation = ({
  hsl,
  hsv,
  onChange,
}: ColorPickerSaturationProps) => {
  const sliderRef = React.useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)

  const calculateSaturation = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!sliderRef.current) {
        return
      }

      const container = sliderRef.current

      const { width: containerWidth, height: containerHeight } =
        container.getBoundingClientRect()

      let x: number
      if ('touches' in e) {
        x = e.touches[0].pageX
      } else {
        x = e.pageX
      }

      let y: number
      if ('touches' in e) {
        y = e.touches[0].pageY
      } else {
        y = e.pageY
      }

      let left = x - (container.getBoundingClientRect().left + window.scrollX)
      let top = y - (container.getBoundingClientRect().top + window.scrollY)

      if (left < 0) {
        left = 0
      } else if (left > containerWidth) {
        left = containerWidth
      }

      if (top < 0) {
        top = 0
      } else if (top > containerHeight) {
        top = containerHeight
      }

      const saturation = left / containerWidth
      const bright = 1 - top / containerHeight

      onChange({
        h: hsl.h,
        s: saturation,
        v: bright,
        a: hsl.a,
        source: 'hsv',
      })
    },
    [onChange, hsl],
  )

  const handleSaturationChange = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      calculateSaturation(e)
    },
    [calculateSaturation],
  )

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    handleSaturationChange(e)
  }

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        handleSaturationChange(e)
      }
    },
    [isDragging, handleSaturationChange],
  )

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    handleSaturationChange(e)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (isDragging) {
      handleSaturationChange(e)
    }
  }

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseUp])

  const clampTop = useMemo(() => `${-(Number(hsv.v) * 100) + 100}%`, [hsv.v])
  const clampLeft = useMemo(() => `${Number(hsv.s) * 100}%`, [hsv.s])

  return (
    <div
      role="spinbutton"
      className="relative mt-4 h-32 w-full rounded-md"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
      style={{
        background: `hsl(${hsl.h},100%, 50%)`,
      }}
    >
      <div
        role="spinbutton"
        className="relative h-32 w-full cursor-pointer overflow-hidden rounded-md"
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        style={{
          background: 'linear-gradient(to right, #fff, rgba(255,255,255,0))',
        }}
      >
        <div
          className="absolute h-32 w-full"
          style={{
            background: 'linear-gradient(to top, #000, rgba(0,0,0,0))',
          }}
        />
      </div>
      <button
        type="button"
        className="-translate-x-1/2 -translate-y-1/2 absolute cursor-grab rounded-full border-2 border-bgPrimaryBase bg-transparent p-1 shadow-md"
        style={{
          top: clampTop,
          left: clampLeft,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      />
    </div>
  )
}
